import type { FetchResult } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  UpdateCardFlagDocument,
  type UpdateCardFlagMutation,
} from 'app-next/[workspace]/cards/components/CardSettingsDialog/update-card-flags.graphql';

import type { CardFlagKey, CardFlags } from '.cache/__types__';

type ToggleFlagArgs = {
  cardId: string | undefined;
  key: CardFlagKey;
  value: boolean;
  otherCardFlags: CardFlags | null | undefined;
};

type UseCardFlagMutationsReturn = {
  toggleFlag: (args: ToggleFlagArgs) => Promise<FetchResult<UpdateCardFlagMutation> | undefined>;
};

export function useCardFlagMutations(): UseCardFlagMutationsReturn {
  const [updateCardFlag] = useMutation(UpdateCardFlagDocument);

  const toggleFlag = async ({ cardId, key, value, otherCardFlags }: ToggleFlagArgs) => {
    if (!cardId) {
      return;
    }

    return await updateCardFlag({
      variables: {
        input: {
          card_id: cardId,
          key: key,
          value: value,
        },
      },
      optimisticResponse: {
        updateCardFlag: {
          flags: {
            ...(otherCardFlags ?? {}),
            [key]: value,
          },
        },
      },
      update: (cache, { data }) => {
        const responseFlag = data?.updateCardFlag;
        if (!responseFlag) return;

        const newFlags = data?.updateCardFlag?.flags;
        if (!newFlags) return;

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            flags(existingFlags) {
              return {
                ...existingFlags,
                ...newFlags,
              };
            },
          },
        });
      },
    });
  };

  return {
    toggleFlag,
  };
}
